import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Loader2, Sparkles, TriangleAlert } from "lucide-react";
import {
  createAiVariable,
  errorMessage,
  testAiVariable,
  updateAiVariable,
  type AiTestResponse,
  type AiVariable,
  type CampaignStep,
  type EmailVariable,
  type VariableFill,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ContactPicker, type PickedContact } from "@/components/email/ContactPicker";
import { VariablePicker } from "@/components/email/VariablePicker";
import { AI_KEY, RENDER_ERROR_LABEL, insertToken, slugifyKey } from "@/lib/email";

const EXAMPLE_PROMPT = `À partir des informations suivantes :

Société : {{company.name}}
Contact : {{contact.full_name}}
Fonction : {{contact.title}}

Écris une phrase de 25 mots maximum expliquant pourquoi cette société est pertinente.
Pas de flatterie. Pas d'invention.`;

/** Create or edit one AI variable of a step, and test it on a real contact:
 *  the resolved input the model receives, and its output. */
export function AiVariableDialog({
  open,
  onOpenChange,
  step,
  variable,
  variables,
  fill,
  slug,
  campaignId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: CampaignStep | null;
  variable: AiVariable | null;
  variables: EmailVariable[];
  fill?: VariableFill | null;
  slug: string;
  campaignId: number;
  onSaved: (step: CampaignStep, key: string) => void;
}) {
  const [key, setKey] = useState("");
  const [prompt, setPrompt] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<AiTestResponse | null>(null);
  const [contact, setContact] = useState<PickedContact | null>(null);
  const [error, setError] = useState<string | null>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  // The caret after an inserted token, placed as soon as the text is written.
  const pendingCaret = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setKey(variable?.key ?? "");
    setPrompt(variable?.prompt_template ?? EXAMPLE_PROMPT);
    setTest(null);
    setError(null);
  }, [open, variable]);

  const keyError = !key
    ? "Give the variable a key."
    : !AI_KEY.test(key)
      ? "Lowercase letters, digits and underscores, starting with a letter."
      : step?.ai_variables.some((v) => v.key === key && v.id !== variable?.id)
        ? `ai.${key} already exists on this step.`
        : null;
  const promptError = !prompt.trim()
    ? "Write the prompt."
    : /\{\{\s*ai\./.test(prompt)
      ? "A prompt can use contact and company variables, not other AI variables."
      : null;

  function insert(token: string, withFallback = false) {
    const el = promptRef.current;
    const start = el?.selectionStart ?? prompt.length;
    const end = el?.selectionEnd ?? prompt.length;
    const next = insertToken(prompt, start, end, token, withFallback);
    pendingCaret.current = next.caret;
    setPrompt(next.text);
  }

  useLayoutEffect(() => {
    const caret = pendingCaret.current;
    if (caret === null) return;
    pendingCaret.current = null;
    promptRef.current?.focus();
    promptRef.current?.setSelectionRange(caret, caret);
  }, [prompt]);

  async function runTest() {
    if (!contact || promptError) return;
    setTesting(true);
    setTest(null);
    try {
      setTest(
        await testAiVariable(slug, {
          contact_id: contact.id,
          key: key || "test",
          prompt_template: prompt,
        }),
      );
    } catch (err) {
      toast.error(errorMessage(err, "The test could not run."));
    } finally {
      setTesting(false);
    }
  }

  async function save() {
    if (!step || keyError || promptError) return;
    setSaving(true);
    setError(null);
    try {
      const saved = variable
        ? await updateAiVariable(slug, variable.id, { key, prompt_template: prompt })
        : await createAiVariable(slug, step.id, { key, prompt_template: prompt });
      toast.success(variable ? `ai.${key} updated` : `ai.${key} created`);
      onSaved(saved, key);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err, "The AI variable could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-5xl flex-col gap-0 overflow-hidden p-0 lg:h-[80vh]">
        <DialogHeader className="border-b border-slate-100 px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-fuchsia-500 to-indigo-500 text-white">
              <Sparkles className="h-3.5 w-3.5" />
            </span>
            {variable ? `Edit ai.${variable.key}` : "New AI variable"}
          </DialogTitle>
          <DialogDescription>
            The model receives only this prompt, with its contact and company variables resolved — nothing else.
          </DialogDescription>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,1fr)] lg:overflow-hidden">
          {/* What the variable is */}
          <div className="space-y-5 px-6 py-5 lg:overflow-y-auto">
            <div className="space-y-1.5">
              <Label htmlFor="ai-key" className="text-xs font-medium text-slate-600">
                Key
              </Label>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  id="ai-key"
                  value={key}
                  placeholder="company_context"
                  onChange={(e) => setKey(e.target.value.includes(" ") ? slugifyKey(e.target.value) : e.target.value.toLowerCase())}
                  onBlur={() => setKey((k) => slugifyKey(k))}
                  className="h-9 max-w-xs font-mono text-sm"
                />
                {key && !keyError && (
                  <span className="rounded bg-fuchsia-50 px-1.5 py-0.5 font-mono text-xs text-fuchsia-700 ring-1 ring-inset ring-fuchsia-200/70">
                    {`{{ai.${key}}}`}
                  </span>
                )}
              </div>
              {keyError && key ? (
                <p className="text-xs text-rose-600">{keyError}</p>
              ) : (
                <p className="text-[11px] text-slate-400">How you insert the passage in the email.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="ai-prompt" className="text-xs font-medium text-slate-600">
                  Prompt
                </Label>
                <VariablePicker variables={variables} fill={fill} onPick={insert} />
              </div>
              <Textarea
                id="ai-prompt"
                ref={promptRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={10}
                className="resize-y rounded-xl border-slate-200 bg-white font-mono text-[13px] leading-relaxed"
              />
              {promptError && prompt ? (
                <p className="text-xs text-rose-600">{promptError}</p>
              ) : (
                <p className="text-[11px] text-slate-400">
                  Contact and company variables are filled in for each contact before the model reads the prompt.
                </p>
              )}
            </div>
          </div>

          {/* Trying it */}
          <aside className="space-y-3 border-t border-slate-100 bg-slate-50/60 px-6 py-5 lg:overflow-y-auto lg:border-l lg:border-t-0">
            <div>
              <p className="text-sm font-semibold text-slate-800">Test on a real contact</p>
              <p className="text-xs text-slate-500">See exactly what the model receives and what it writes. Nothing is saved.</p>
            </div>
            <ContactPicker slug={slug} campaignId={campaignId} value={contact} onChange={setContact} />
            <Button
              variant="outline"
              className="w-full bg-white"
              disabled={!contact || !!promptError || testing}
              onClick={() => void runTest()}
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
              {contact ? "Run the test" : "Pick a contact to test"}
            </Button>
            {test && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400">
                    Output{test.model && <span className="normal-case tracking-normal text-slate-400"> · {test.model}</span>}
                  </p>
                  {test.output ? (
                    <div className="rounded-lg bg-gradient-to-br from-fuchsia-50 to-indigo-50 p-3 text-sm leading-relaxed text-slate-800 ring-1 ring-fuchsia-100">
                      {test.output}
                    </div>
                  ) : (
                    <p className="rounded-lg bg-rose-50 p-3 text-xs text-rose-700">
                      {test.error ?? "Not generated: the prompt has errors to fix first."}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400">Prompt as the model reads it</p>
                  <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-lg bg-white p-3 text-xs leading-relaxed text-slate-700 ring-1 ring-slate-100">
                    {test.input_resolved}
                  </pre>
                  {test.errors.map((e) => (
                    <p key={`${e.kind}-${e.token}`} className="flex items-center gap-1 text-xs text-rose-600">
                      <TriangleAlert className="h-3 w-3" />
                      {RENDER_ERROR_LABEL[e.kind] ?? e.kind}
                      {e.token && <span className="font-mono">{e.token}</span>}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>

        <DialogFooter className="gap-2 border-t border-slate-100 px-6 py-3.5">
          {error && <p className="mr-auto self-center text-sm text-rose-600">{error}</p>}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!step || !!keyError || !!promptError || saving} onClick={() => void save()}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {variable ? "Save" : "Create variable"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
